import { categoryPathLabel, type RoomDocument } from '@dataroom/shared';

/**
 * The documents the buyer may see at the current stage. Each row is marked
 * "View only" unless the server's `canDownload` says the buyer's grant allows
 * downloading it. A row opens the protected, in-place DocumentViewer (issue #14)
 * via `onOpen`; the list itself never offers the bytes — the viewer's Download
 * control (shown only when `canDownload`) does.
 *
 * Rows are read defensively: a malformed row (e.g. a search hit missing its
 * `name` or `categoryPath`) renders as a plain "Untitled document" row instead
 * of throwing and blanking the whole room page.
 */
export function DocumentList({
  documents,
  onOpen,
  emptyMessage = 'No documents are available to you yet.',
}: {
  documents: RoomDocument[];
  onOpen: (doc: RoomDocument) => void;
  emptyMessage?: string;
}) {
  if (documents.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <ul className="divide-y">
      {documents.map((doc) => {
        const prepared = doc.beingPrepared === true;
        const name = doc.name || (prepared ? 'Document being prepared' : 'Untitled document');
        const categoryPath = Array.isArray(doc.categoryPath) ? doc.categoryPath : [];
        return (
          <li key={doc.id}>
            <button
              type="button"
              onClick={() => onOpen(doc)}
              disabled={prepared}
              aria-label={prepared ? `${name}, being prepared` : `View ${name}`}
              className="flex w-full items-center justify-between gap-4 py-2.5 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{name}</p>
                {categoryPath.length > 0 ? (
                  <p className="truncate text-xs text-muted-foreground">
                    {categoryPathLabel(categoryPath)}
                  </p>
                ) : null}
              </div>
              <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                {prepared ? 'Being prepared' : doc.canDownload ? 'Download allowed' : 'View only'}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
